<template>
  <div
    v-if="columnData.widgets === null || (columnData.widgets && columnData.widgets.length === 0)"
    class="stats-builder-col"
    :class="{ 'enabledCol': (selectedWidgetState !== null) && (rowId === selectedWidgetState.location.rowId && columnData.id === selectedWidgetState.location.columnId
                && (selectedWidgetState.location.panelData === undefined || selectedWidgetState.location.panelData === null)),
              'emptyCol': columnData.widgets === null || (columnData.widgets && columnData.widgets.length === 0),
              'col-12': columnData.layout === StatsBuilderColumnLayoutEnum.TWELVE,
              'col-9': columnData.layout === StatsBuilderColumnLayoutEnum.NINE,
              'col-6': columnData.layout === StatsBuilderColumnLayoutEnum.SIX,
              'col-4': columnData.layout === StatsBuilderColumnLayoutEnum.FOUR,
              'col-3': columnData.layout === StatsBuilderColumnLayoutEnum.THREE,
              'align-items-center': !rowHasColumnWithMultipleWidgets,
              'align-items-start': rowHasColumnWithMultipleWidgets }"
    style="position: relative;"
  >
    <div
      v-if="showRemoveColumnAction"
      class="sb-col-actions"
    >
      <div class="flex flex-column">
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.column.remove')"
          class="flex sb-action mb-1"
          @click="removeEmptyColumn"
        >
          <i class="far fa-trash" />
        </div>
      </div>
    </div>
    <div
      class="flex justify-content-center sb-col-content"
    >
      <draggable
        v-if="columnData.widgets === null || (columnData.widgets && columnData.widgets.length === 0)"
        class="emptyColumnSlot"
        style="width: 100%;"
        :list="[{ id: 1 }]"
        :data-column-layout="columnData.layout"
        :data-location="JSON.stringify({ rowId, columnId: columnData.id, panelData: null })"
        v-bind="dragOnEmptyColumnOptions"
      >
        <template #item="{ element }">
          <div
            class="emptyColumnSlot__content"
            :data-id-test="element.id"
          >
            <div class="emptyColumnSlot__status emptyColumnSlot__status--positive">
              <i class="fa-solid fa-circle-check emptyColumnSlot__status-icon" />
              <span>{{ t('statsEditor.placeholders.ready') }}</span>
            </div>
            <div class="emptyColumnSlot__status emptyColumnSlot__status--negative">
              <i class="fa-solid fa-circle-xmark emptyColumnSlot__status-icon" />
              <span>{{ t('statsEditor.placeholders.notCompatible') }}</span>
            </div>
            <div class="emptyColumnSlot__visual" aria-hidden="true">
              <i class="fa-solid fa-arrow-down-to-line" />
            </div>
            <div class="emptyColumnSlot__copy">
              <span class="emptyColumnSlot__eyebrow">{{ t('statsEditor.placeholders.dropHere') }}</span>
            </div>
          </div>
        </template>
      </draggable>
    </div>
  </div>
  <div
    v-else
    class="stats-builder-col"
    :class="{ 'enabledCol': (selectedWidgetState !== null) && (rowId === selectedWidgetState.location.rowId && columnData.id === selectedWidgetState.location.columnId
                && (selectedWidgetState.location.panelData === undefined || selectedWidgetState.location.panelData === null)),
              'emptyCol': columnData.widgets === null,
              'col-12': columnData.layout === StatsBuilderColumnLayoutEnum.TWELVE,
              'col-9': columnData.layout === StatsBuilderColumnLayoutEnum.NINE,
              'col-6': columnData.layout === StatsBuilderColumnLayoutEnum.SIX,
              'col-4': columnData.layout === StatsBuilderColumnLayoutEnum.FOUR,
              'col-3': columnData.layout === StatsBuilderColumnLayoutEnum.THREE,
              'align-items-center': !rowHasColumnWithMultipleWidgets,
              'align-items-start': rowHasColumnWithMultipleWidgets }"
    style="position: relative;"
  >
    <draggable
      :key="JSON.stringify(dragElementsOptions)"
      :list="columnData.widgets"
      handle=".handle-widget"
      class="w-full sb-col-content flex flex-column gap-1"
      :data-from-widget="true"
      :data-location="JSON.stringify({ rowId: rowId, columnId: columnData.id, columnLayout: columnData.layout })"
      :data-has-menu="hasMenu"
      v-bind="dragElementsOptions"
    >
      <template #item="{ element, index }">
        <StatsBuilderColItem
          :element="element"
          :index="index"
          :column-data="columnData"
          :row-id="rowId"
          @edit-widget="editWidget"
          @remove-widget="removeWidget"
        />
      </template>
    </draggable>
  </div>
  <ConfirmDialog :group="`removeWidget_${rowId}_${columnData.id}`" />
  <ConfirmDialog :group="`removeColumn_${rowId}_${columnData.id}`" />
</template>

<script lang="ts">
import {
  computed, defineComponent,
  Ref,
  ref, SetupContext,
} from 'vue';
import { StatsBuilderColumnLayoutEnum, StatsEditorPanelEnum, StatsWidgets } from '@/types';
import StatsWidgetMetadata, { getComponentStatsWidgetMetadata } from '@/components/stats-builder/metadata';
import { StatsBuilderSelectedWidget } from '@/types/stats-editor-types';
import { useStore } from '@/store';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import StatsBuilderColItem from '@/components/stats-builder/StatsBuilderColItem.vue';
import { removePlaceholderClass } from '@/helpers';

export default defineComponent({
  name: 'StatsBuilderCol',

  components: {
    draggable,
    ConfirmDialog,
    StatsBuilderColItem,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    rowId: {
      type: String,
      required: true,
    },

    columnData: {
      type: Object,
      required: true,
    },

    rowChildrenCount: {
      type: Number,
      default: 1,
    },

    rowHasColumnWithMultipleWidgets: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hover:col'],

  setup(props: { rowId: string; columnData: any; rowChildrenCount: number }, { emit }: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const confirm = useConfirm();

    const selectedWidgetState: Ref<StatsBuilderSelectedWidget|null> = computed(() => store.getters['statsEditor/getSelectedWidget']);

    const pageTypeState = computed(() => store.getters['statsEditor/getPageType']);

    const availableElements: any = computed(() => Object.values(StatsWidgetMetadata)
      .filter((item: any) => item.StatsWidgetMeta.availability.includes(pageTypeState.value))
      .map((item: any) => ({
        type: 'placeholder',
        widgetType: item.StatsWidgetMeta.component,
        iconClass: item.StatsWidgetMeta.icon,
        label: item.StatsWidgetMeta.label,
        group: 'widget',
        minColumnsRequired: item.StatsWidgetMeta.min_columns_required,
      })));

    const dragOnEmptyColumnOptions = ref({
      animation: 200,
      sort: true,
      group: { name: 'empty_column', pull: false, put: ['widget_element', 'statsWidget'] },
      disabled: false,
      onAdd: (evt: any) => {
        evt.preventDefault();
        if (evt.from.getAttribute('data-from-widget')) {
          const dataWidget = JSON.parse(evt.clone.getAttribute('data-widget'));
          const dataWidgetLocation = JSON.parse(evt.clone.getAttribute('data-widget-location'));
          const dataLocation = JSON.parse(evt.to.getAttribute('data-location'));
          const dataColumnLayout = JSON.parse(evt.to.getAttribute('data-column-layout'));

          const widgetType = availableElements.value.find((item: any) => item.widgetType === dataWidget.type);
          if (parseInt(widgetType.minColumnsRequired, 10) > dataColumnLayout) {
            store.dispatch('statsEditor/undoMoveWidget', {
              dataWidget,
              dataWidgetLocation,
            });
          } else {
            store.dispatch('statsEditor/addWidgetToEmptyCol', {
              dataWidget,
              dataLocation,
            });
          }
        } else if (evt.from.getAttribute('data-from-subwidget')) {
          const dataWidget = JSON.parse(evt.clone.getAttribute('data-widget'));
          const dataLocation = JSON.parse(evt.to.getAttribute('data-location'));
          const dataLastLocation = JSON.parse(evt.clone.getAttribute('data-location'));

          store.dispatch('statsEditor/addWidgetToEmptyCol', {
            dataWidget,
            dataLocation,
            dataLastLocation,
          });
        }
      },
    });

    const isColumnEmpty = computed(() => {
      if (!props.columnData.widgets || props.columnData.widgets.length === 0) {
        return true;
      }
      return false;
    });

    const showRemoveColumnAction = computed(() => props.rowChildrenCount > 1 && isColumnEmpty.value);

    const editWidget = (index: number) => {
      const meta = getComponentStatsWidgetMetadata(props.columnData?.widgets[index]?.type);
      if (!meta) {
        return;
      }

      const selectedWidget: StatsBuilderSelectedWidget = {
        component: props.columnData?.widgets[index]?.type,
        data: JSON.parse(JSON.stringify(props.columnData?.widgets[index])),
        location: {
          rowId: props.rowId,
          columnId: props.columnData.id,
          panelData: null,
          columnLayout: props.columnData.layout,
          widgetIndex: index,
        },
      };

      store.commit('statsEditor/setSelectedWidget', selectedWidget);
      store.commit('statsEditor/showLeftToolbar', StatsEditorPanelEnum.WIDGET_EDIT_PANEL);
    };

    const removeWidget = (index: number) => {
      confirm.require({
        message: t('statsEditor.confirmationMessages.removeWidget'),
        header: 'Confirmation',
        group: `removeWidget_${props.rowId}_${props.columnData.id}`,
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          store.dispatch('statsEditor/removeWidget', {
            location: {
              rowId: props.rowId,
              columnId: props.columnData.id,
              widgetIndex: index,
            },
          });
        },
      });
    };

    const removeEmptyColumn = () => {
      if (!showRemoveColumnAction.value) {
        return;
      }

      confirm.require({
        message: t('statsEditor.confirmationMessages.removeColumn'),
        header: 'Confirmation',
        group: `removeColumn_${props.rowId}_${props.columnData.id}`,
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          store.dispatch('statsEditor/removeColumn', {
            rowId: props.rowId,
            columnId: props.columnData.id,
            subColumnId: null,
          });
        },
      });
    };

    // Prevent dropping widget into col with menu widgets
    const hasMenu = computed(() => {
      if (props.columnData.hasOwnProperty('widgets')) {
        return props.columnData.widgets.some((widget: any) => [StatsWidgets.MENU, StatsWidgets.TABS].includes(widget.type));
      }
      return false;
    });

    const dragElementsOptions = computed(() => ({
      animation: 200,
      sort: true,
      group: {
        name: 'statsWidget',
        pull: true,
        put: hasMenu.value
          ? false
          : true,
      },
      disabled: false,
      onStart: () => {
        store.commit('statsEditor/setIsDraggingBuilderItem', true);
      },
      onEnd: () => {
        store.commit('statsEditor/setIsDraggingBuilderItem', false);
      },
      onAdd: (evt: any) => {
        if (evt.from.getAttribute('data-from-subwidget')) {
          const dataLastLocation = JSON.parse(evt.clone.getAttribute('data-location'));
          store.dispatch('statsEditor/removeWidgetFromSubCol', {
            dataLastLocation,
          });
        }
        const dataWidget = JSON.parse(evt.clone.getAttribute('data-widget'));
        const dataWidgetLocation = JSON.parse(evt.clone.getAttribute('data-widget-location')) || JSON.parse(evt.clone.getAttribute('data-location'));
        let location = evt.to.getAttribute('data-location');

        if (location && dataWidget) {
          location = JSON.parse(location);
          const widgetType = availableElements.value.find((item: any) => item.widgetType === dataWidget.type);
          const destinationHasMenu = evt.to.getAttribute('data-has-menu');
          const isMenuWidget = [StatsWidgets.MENU, StatsWidgets.TABS].includes(dataWidget.type);

          if (parseInt(widgetType.minColumnsRequired, 10) > location.columnLayout
            || (destinationHasMenu === 'true' && isMenuWidget)) {
            store.dispatch('statsEditor/undoMoveWidget', {
              dataWidget,
              dataWidgetLocation,
            });
            store.dispatch('statsEditor/removeWidget', {
              location: {
                rowId: location.rowId,
                columnId: location.columnId,
                widgetIndex: evt.newIndex,
              },
            });
          }
        }
      },
      onMove: (evt: any) => {
        // On move function for widgets inside column and sub column
        evt.preventDefault();
        if (evt.dragged.getAttribute('data-widget-location')) {
          const dataLocation = JSON.parse(evt.dragged.getAttribute('data-widget-location'));
          const dataWidget = JSON.parse(evt.dragged.getAttribute('data-widget'));

          const choosedWidgetForDrag: Record<string, any> = {
            rowId: dataLocation.rowId,
            columnId: dataLocation.columnId,
            index: dataLocation?.widgetIndex,
          };
          if (dataLocation.panelData && typeof dataLocation.panelData.widgetIndex === 'number') {
            choosedWidgetForDrag.index = dataLocation.panelData.widgetIndex;
            choosedWidgetForDrag.panelData = {
              panelId: dataLocation.panelData.panelId,
              subRowId: dataLocation.panelData.subRowId,
              subColumnId: dataLocation.panelData.subColumnId,
              widgetIndex: dataLocation.panelData.widgetIndex,
            };
          }

          store.commit('statsEditor/setChoosedWidgetForDrag', choosedWidgetForDrag);

          removePlaceholderClass(evt.dragged);

          if (evt.from !== evt.to) {
            const columnDestinationLayout = evt.to.getAttribute('data-column-layout');
            const destination = evt.to.getAttribute('data-from-widget') || evt.to.getAttribute('data-from-subwidget');

            const widgetType = availableElements.value.find((item: any) => item.widgetType === dataWidget.type);
            const isMenuWidget = [StatsWidgets.MENU, StatsWidgets.TABS].includes(dataWidget.type);

            // Check if destination is empty column
            if (columnDestinationLayout) {
              const isOnPanel = evt.to.getAttribute('data-is-on-panel');

              if ((parseInt(widgetType.minColumnsRequired, 10) > parseInt(columnDestinationLayout, 10))
                || (isOnPanel && isMenuWidget)) {
                evt.to.classList.add('notCompatibleSlot');
              } else {
                evt.to.classList.add('chosenSlot');
              }
            } else if (destination) {
              // Check if destination is inside column with widgets
              const location = evt.to.getAttribute('data-location');
              const dataHasMenu = evt.to.getAttribute('data-has-menu');

              if (location) {
                const locationObject = JSON.parse(location);
                if ((parseInt(widgetType.minColumnsRequired, 10) > parseInt(locationObject.columnLayout, 10))
                  || (isMenuWidget && locationObject.panelData) || (dataHasMenu === 'true' && isMenuWidget)) {
                  evt.dragged.classList.add('placeholder-on-add-widget', 'placeholder-on-add-widget--disallowed', 'm-4');
                } else {
                  evt.dragged.classList.add('placeholder-on-add-widget', 'placeholder-on-add-widget--allowed', 'm-4');
                }
              }
            }
          } else {
            evt.dragged.classList.add('placeholder-on-add-widget', 'placeholder-on-add-widget--allowed', 'm-4');
          }
        }
        return true;
      },
      onUnchoose: (evt: any) => {
        removePlaceholderClass(evt.item);

        store.commit('statsEditor/resetChoosedWidgetForDrag');
        store.commit('statsEditor/setIsDraggingBuilderItem', false);
      },
    }));

    return {
      t,
      dragOnEmptyColumnOptions,
      selectedWidgetState,
      StatsBuilderColumnLayoutEnum,
      dragElementsOptions,
      hasMenu,
      showRemoveColumnAction,

      editWidget,
      removeWidget,
      removeEmptyColumn,
    };
  },
});
</script>

<style lang="scss">
$brand-green: #94c840;
$brand-green-dark: #36520f;
$brand-green-mid: #5f8f16;
$error-red: #c84040;

#pageRowsList .stats-builder-col {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 0;
  height: 100%; // Force la hauteur totale pour que les widgets puissent s'étendre
  align-items: stretch; // Étire les enfants sur toute la hauteur

  .sb-col-content {
    width: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    align-items: stretch;
    justify-content: center;
    position: relative;
    height: 100%; // Force la hauteur totale
  }

  &.emptyCol > .sb-col-content {
    min-height: 132px;
    padding: 1.35rem 1.6rem;
    border-radius: 1.5rem;
    border: 1px solid rgba($brand-green, 0.22);
    background: linear-gradient(140deg, rgba($brand-green, 0.12), rgba($brand-green, 0.03));
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
    transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  }

  &.colInHover:not(.enabledCol) > .sb-col-content {
    border-color: rgba($brand-green, 0.4);
    background: linear-gradient(140deg, rgba($brand-green, 0.18), rgba($brand-green, 0.06));
    box-shadow: 0 16px 36px -26px rgba($brand-green-dark, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.65);
  }

  &.enabledCol:not(.emptyCol) > .sb-col-content {
    background: none;
    box-shadow: none;
    border-color: transparent;
  }
}

#pageRowsList .sb-col-content .vue-recycle-scroller__item-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

#pageRowsList .emptyColumnSlot {
  --slot-border: rgba(148, 200, 64, 0.42);
  --slot-fill: rgba(244, 249, 236, 0.95);
  --slot-highlight: rgba(148, 200, 64, 0.18);
  --slot-shadow: rgba(39, 65, 9, 0.28);
  --slot-text: #2f353b;
  --slot-icon-bg: rgba(148, 200, 64, 0.18);
  --slot-icon-color: #4f7013;
  --slot-chip-bg: rgba(148, 200, 64, 0.16);
  --slot-chip-alt: rgba(148, 200, 64, 0.1);
  --slot-chip-color: #375012;
  position: relative;
  display: flex;
  width: 100%;
  border-radius: 1.4rem;
  padding: 2px;
  background: linear-gradient(140deg, rgba($brand-green, 0.14), rgba($brand-green, 0.03));
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  isolation: isolate;

  &__content {
    position: relative;
    display: flex;
    align-items: center;
    gap: 1.65rem;
    width: 100%;
    padding: 1.7rem 1.9rem;
    border-radius: 1.3rem;
    border: 1px solid var(--slot-border);
    background: linear-gradient(140deg, rgba(255, 255, 255, 0.96) 0%, var(--slot-fill) 100%);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
    overflow: hidden;
    transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;

    &::before {
      content: '';
      position: absolute;
      inset: 0.35rem;
      border-radius: inherit;
      background:
        radial-gradient(circle at 16% 24%, rgba(148, 200, 64, 0.16) 0%, transparent 60%),
        radial-gradient(circle at 84% 18%, rgba(148, 200, 64, 0.12) 0%, transparent 55%);
      opacity: 0.85;
      pointer-events: none;
      z-index: 0;
    }

    &::after {
      content: '';
      position: absolute;
      left: 1.8rem;
      right: 1.8rem;
      bottom: 0.95rem;
      height: 4px;
      border-radius: 999px;
      background: linear-gradient(90deg, rgba(148, 200, 64, 0.28), rgba(148, 200, 64, 0.55));
      opacity: 0;
      transform: scaleX(0.6);
      transform-origin: left center;
      transition: opacity 0.25s ease, transform 0.25s ease;
      pointer-events: none;
      z-index: 1;
    }

    > * {
      position: relative;
      z-index: 2;
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 46px -32px rgba($brand-green-dark, 0.38);
  }

  &:hover .emptyColumnSlot__content {
    border-color: rgba($brand-green, 0.68);
    box-shadow: 0 20px 48px -32px rgba($brand-green-dark, 0.42), inset 0 0 0 1px rgba(255, 255, 255, 0.6);
  }

  &:hover .emptyColumnSlot__content::after {
    opacity: 1;
    transform: scaleX(1);
  }

  &__visual {
    position: relative;
    display: grid;
    place-items: center;
    width: 3.3rem;
    height: 3.3rem;
    border-radius: 1rem;
    background: var(--slot-icon-bg);
    color: var(--slot-icon-color);
    border: 1px solid rgba($brand-green, 0.22);
    box-shadow: 0 18px 32px -26px var(--slot-shadow);
    transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;

    &::after {
      content: '';
      position: absolute;
      inset: -0.3rem;
      border-radius: inherit;
      border: 1px solid currentColor;
      opacity: 0.15;
    }

    i {
      font-size: 1.25rem;
    }
  }

  &:hover .emptyColumnSlot__visual {
    transform: translateY(-1px) scale(1.04);
  }

  &__copy {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 24rem;
    color: var(--slot-text);
  }

  &__eyebrow {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-weight: 650;
    color: rgba(57, 76, 21, 0.75);
  }

  &__description {
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  &__hints {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    margin-top: 0.4rem;
  }

  &__hint {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.38rem 0.75rem;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 650;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: var(--slot-chip-bg);
    color: var(--slot-chip-color);
    transition: transform 0.25s ease, background 0.25s ease;

    i {
      font-size: 0.78rem;
    }
  }

  &__hint--secondary {
    background: var(--slot-chip-alt);
  }

  &:hover .emptyColumnSlot__hint {
    transform: translateY(-1px);
  }

  &__status {
    position: absolute;
    top: 1.05rem;
    right: 1.05rem;
    display: inline-flex;
    align-items: center;
    gap: 0.38rem;
    padding: 0.4rem 0.85rem;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.97);
    border: 1px solid var(--slot-border);
    color: var(--slot-chip-color);
    box-shadow: 0 16px 32px -28px rgba($brand-green-dark, 0.45);
    opacity: 0;
    transform: translateY(-8px);
    transition: opacity 0.25s ease, transform 0.25s ease, border-color 0.25s ease, color 0.25s ease;
    z-index: 3;

    &-icon {
      font-size: 0.82rem;
    }
  }

  &__status--negative {
    border-color: rgba($error-red, 0.4);
    color: rgba($error-red, 0.85);
  }

  &.chosenSlot {
    --slot-border: rgba(148, 200, 64, 0.82);
    --slot-fill: rgba(236, 248, 216, 0.97);
    --slot-highlight: rgba(148, 200, 64, 0.28);
    --slot-icon-bg: $brand-green;
    --slot-icon-color: #ffffff;
    --slot-chip-bg: rgba(148, 200, 64, 0.24);
    --slot-chip-alt: rgba(148, 200, 64, 0.18);
    --slot-chip-color: #2f4310;
    transform: translateY(-2px);
    box-shadow: 0 26px 56px -34px rgba($brand-green-dark, 0.5);
  }

  &.chosenSlot .emptyColumnSlot__content {
    border-color: $brand-green;
    box-shadow: 0 24px 52px -32px rgba($brand-green-dark, 0.48), inset 0 0 0 1px rgba(255, 255, 255, 0.62);
  }

  &.chosenSlot .emptyColumnSlot__content::after {
    opacity: 1;
    transform: scaleX(1);
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.55), rgba($brand-green, 0.85));
  }

  &.chosenSlot .emptyColumnSlot__visual {
    transform: translateY(-2px) scale(1.05);
  }

  &.chosenSlot .emptyColumnSlot__status--positive {
    opacity: 1;
    transform: translateY(0);
  }

  &.notCompatibleSlot {
    --slot-border: rgba($error-red, 0.45);
    --slot-fill: rgba(254, 245, 245, 0.97);
    --slot-highlight: rgba($error-red, 0.18);
    --slot-icon-bg: rgba($error-red, 0.18);
    --slot-icon-color: $error-red;
    --slot-chip-bg: rgba($error-red, 0.18);
    --slot-chip-alt: rgba($error-red, 0.12);
    --slot-chip-color: #701f1f;
    background: linear-gradient(140deg, rgba($error-red, 0.2), rgba($error-red, 0.05));
    box-shadow: 0 20px 46px -30px rgba(116, 24, 24, 0.45);
  }

  &.notCompatibleSlot .emptyColumnSlot__content {
    border-color: rgba($error-red, 0.55);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6);
  }

  &.notCompatibleSlot .emptyColumnSlot__content::after {
    opacity: 1;
    background: linear-gradient(90deg, rgba($error-red, 0.3), rgba($error-red, 0.55));
  }

  &.notCompatibleSlot .emptyColumnSlot__visual {
    transform: translateY(-1px) scale(0.96);
  }

  &.notCompatibleSlot .emptyColumnSlot__status--negative {
    opacity: 1;
    transform: translateY(0);
  }

  &.notCompatibleSlot .emptyColumnSlot__hint {
    background: rgba($error-red, 0.12);
    color: #701f1f;
  }

  &.notCompatibleSlot .emptyColumnSlot__hint--secondary {
    background: rgba($error-red, 0.08);
  }
}

@media (max-width: 1440px) {
  #pageRowsList .stats-builder-col.emptyCol > .sb-col-content {
    padding: 1.1rem 1.35rem;
  }

  #pageRowsList .emptyColumnSlot__content {
    padding: 1.55rem 1.7rem;
    gap: 1.4rem;
  }
}

@media (max-width: 1024px) {
  #pageRowsList .emptyColumnSlot__content {
    flex-direction: column;
    align-items: flex-start;
  }

  #pageRowsList .emptyColumnSlot__status {
    position: static;
    align-self: flex-end;
    margin-bottom: 0.4rem;
    transform: translateY(0);
  }

  #pageRowsList .emptyColumnSlot__visual {
    width: 3rem;
    height: 3rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  #pageRowsList .stats-builder-col,
  #pageRowsList .emptyColumnSlot,
  #pageRowsList .emptyColumnSlot__content,
  #pageRowsList .emptyColumnSlot__hint,
  #pageRowsList .emptyColumnSlot__visual {
    transition: none !important;
  }
}

</style>
